
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import { apiRequests } from "src/shared/api/api";

dayjs.extend(relativeTime);
dayjs.locale("ru");

export const QUERY_KEYS = {
  CHATS: (token, filters) => ['chats', token, filters],
  CHAT_MESSAGES: (chatId, token) => ['chatMessages', chatId, token],
  CHANNELS: (token) => ['channels', token],
  CHAT_FILES: (chatId) => ['chatFiles', chatId],
  CONTRAGENTS: (token, search) => ['contragents', token, search]
};

export const fetchChats = async ({ queryKey }) => {
  const [, token, filters] = queryKey;
  const params = {
    token,
    limit: filters?.limit || 100,
    sort_by: filters?.sort_by || 'last_message_time',
    sort_order: filters?.sort_order || 'desc',
    ...filters
  };
  
  const response = await apiRequests.chats.getChats(params);
  return response.data || [];
};

export const fetchChannels = async ({ queryKey }) => {
  const [, token] = queryKey;
  const response = await apiRequests.chats.getChannels({ token });
  return response.data || [];
};

export const fetchChatMessages = async ({ queryKey }) => {
  const [, chatId, token] = queryKey;
  const response = await apiRequests.chats.getChatMessages(chatId, { 
    token,
    limit: 50 
  });
  return response.data?.data || [];
};

export const getSenderType = () => {
  return localStorage.getItem("sender_type") || "OPERATOR";
};

export const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = dayjs(timestamp);
  const now = dayjs();
  
  if (date.isSame(now, "day")) {
    return date.format("HH:mm");
  } else if (date.isSame(now.subtract(1, "day"), "day")) {
    return "Вчера";
  } else if (date.isSame(now, "year")) {
    return date.format("D MMM");
  } else {
    return date.format("D.MM.YY");
  }
};

export const formatLastMessageTime = (timeString) => {
  if (!timeString) return "";
  
  try {
    const messageTime = dayjs(timeString);
    const now = dayjs();
    
    if (messageTime.isSame(now, "day")) {
      return messageTime.format("HH:mm");
    } else if (messageTime.isSame(now.subtract(1, "day"), "day")) {
      return "Вчера";
    } else if (messageTime.isSame(now, "year")) {
      return messageTime.format("D MMM");
    } else {
      return messageTime.format("DD.MM.YY");
    }
  } catch (error) {
    console.error("Error parsing time:", error);
    return "";
  }
};


let notificationAudioContext = null;

/**
 * Вызвать при первом клике/касании пользователя по странице чатов.
 * Разблокирует воспроизведение звука (политика autoplay браузера).
 */
export const unlockAudioForNotifications = () => {
  if (notificationAudioContext) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      notificationAudioContext = new Ctx();
      if (notificationAudioContext.state === "suspended") {
        notificationAudioContext.resume().then(() => {}).catch(() => {});
      }
    }
  } catch (e) {}
};

function isSoundNotificationsEnabled() {
  const v = localStorage.getItem('chat_sound_notifications');
  if (v == null) return true;
  return String(v).toLowerCase() !== 'false';
}

/**
 * Воспроизводит короткий звук уведомления через Web Audio API (без файла).
 */
export const playNotificationSound = () => {
  try {
    if (!isSoundNotificationsEnabled()) return;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;

    const ctx = notificationAudioContext || new Ctx();
    if (ctx.state === "suspended") {
      ctx.resume().then(() => playBeep(ctx)).catch(() => {});
      return;
    }
    playBeep(ctx);
  } catch (error) {
    console.debug('Ошибка при воспроизведении звука уведомления:', error);
  }
};

function playBeep(ctx) {
  try {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = 880;
    oscillator.type = "sine";
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.25);
    oscillator.start(t);
    oscillator.stop(t + 0.25);
  } catch (e) {
    console.debug('playBeep error:', e);
  }
}

export const getWebSocketBaseURL = () => {
  const baseUrl = process.env.REACT_APP_APP_URL || window.location.hostname;
  const wsUrl = `wss://${baseUrl}`;
  return wsUrl;
};

export const processImageData = (data) => {
  if (!data) return null;
  
  
  if (data.startsWith('data:image')) {
    return data;
  }
  
  
  
  let mimeType = 'image/jpeg'; 
  
  if (data.startsWith('/9j/') || data.startsWith('/9j/4AA')) {
    mimeType = 'image/jpeg';
  } else if (data.startsWith('iVBORw0KGgo')) {
    mimeType = 'image/png';
  } else if (data.startsWith('R0lGOD')) {
    mimeType = 'image/gif';
  }
  
  return `data:${mimeType};base64,${data}`;
};


export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

export const truncatePreview = (text, maxLength = 40) => {
  if (!text) return "Нет сообщений";
  
  if (text.startsWith('data:image')) {
    return "Изображение";
  }
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength - 3) + "...";
};

const IMAGE_PREVIEW_PLACEHOLDERS = ['[photo]', '[image]', '[изображение]', 'изображение'];
export const isImageMessage = (text) => {
  if (!text) return false;
  if (text.startsWith('data:image')) return true;
  const normalized = (text || '').trim().toLowerCase();
  return normalized === 'изображение' || IMAGE_PREVIEW_PLACEHOLDERS.some(p => normalized === p);
};

export function buildPhotoUrl(path) {
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  const apiBase = `https://${process.env.REACT_APP_APP_URL || "app.tablecrm.com"}`;
  const normalized = path.startsWith("/") ? path.slice(1) : path;
  if (normalized.startsWith("api/v1/photos/")) return `${apiBase}/${normalized}`;
  if (normalized.startsWith("photos/")) return `${apiBase}/api/v1/photos/${normalized}`;
  return null;
}